import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AlertCircle,
  FileSpreadsheet,
  LucideAngularModule,
  Package,
  Upload,
  X,
} from 'lucide-angular';
import { Observable } from 'rxjs';
import {
  ExcelPreview,
  ProductImportMapping,
  ProductImportResult,
} from '../../../core/models/admin.models';

/** Do'kon tanlagichi uchun minimal shakl — chaqiruvchi o'z DTO'sidan moslaydi. */
export interface ImportStoreOption {
  id: string;
  name: string;
}

/**
 * `ApiResponse` va (eskirgan) `BaseResponse` ning umumiy shakli — komponent
 * ikkalasini ham qabul qiladi, chunki admin va mijoz servislari turlicha yozilgan.
 */
export interface ImportResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

/** Valyuta tanlagichi uchun variant — `CurrencyService.currencyOptions()` bilan bir xil shakl. */
export interface ImportCurrencyOption {
  label: string;
  value: string;
}

/**
 * Fayldagi narxlar qaysi valyutada. Butun fayl uchun BITTA tanlov — katalog
 * fayllarida valyuta ustuni deyarli uchramaydi, qatorma-qator so'rash esa
 * foydalanuvchidan mavjud bo'lmagan ma'lumotni talab qilardi.
 *
 * `null` — backend tenant kotirovka valyutasini oladi (eski xulq).
 */
export interface ImportCurrencies {
  sellCurrency: string | null;
  purchaseCurrency: string | null;
}

export type ImportPreviewFn = (file: File) => Observable<ImportResponse<ExcelPreview>>;
export type ImportSubmitFn = (
  file: File,
  mapping: ProductImportMapping,
  storeId: string | null,
  currencies: ImportCurrencies,
) => Observable<ImportResponse<ProductImportResult>>;

/**
 * Excel'dan mahsulotlarni ommaviy yuklash oynasi.
 *
 * <b>So'rovlarni O'ZI yubormaydi</b> — `previewFn`/`submitFn` chaqiruvchidan keladi.
 * Sabab: admin paneli `/api/admin/...` endpointlarini (tenant id ochiq beriladi),
 * mijozning o'z sahifasi esa `/api/products/import` ni ishlatadi (tenant JWT dan).
 * Oyna ikkalasi uchun bir xil bo'lgani uchun bu yerda faqat UI va moslashtirish
 * mantiqi turadi.
 */
@Component({
  selector: 'app-product-import-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, TranslatePipe],
  template: `
    @if (visible()) {
      <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
        <div
          class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] flex flex-col"
        >
          <div
            class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0"
          >
            <div class="flex items-center gap-2">
              <lucide-icon [img]="Package" class="w-5 h-5 text-indigo-600" />
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
                {{ 'PRODUCT_IMPORT.TITLE' | translate }}
                @if (subtitle()) {
                  <span class="font-normal text-gray-500"> — {{ subtitle() }}</span>
                }
              </h2>
            </div>
            <button
              (click)="close()"
              class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              <lucide-icon [img]="X" class="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div class="p-5 space-y-4 overflow-y-auto">
            <!--
              Faylni tayyorlash qoidalari. Oynaning ENG BOSHIDA turadi: qoidalar
              faqat yuklashdan keyin xato ro'yxati bo'lib chiqsa, egasi faylni bir
              necha marta qayta tayyorlashga majbur bo'ladi.
            -->
            <div
              class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-900 dark:text-blue-200"
            >
              <p class="font-semibold mb-1.5">{{ 'PRODUCT_IMPORT.NOTE.TITLE' | translate }}</p>
              <p class="mb-1">• {{ 'PRODUCT_IMPORT.NOTE.REQUIRED' | translate }}</p>
              <p class="mb-1">• {{ 'PRODUCT_IMPORT.NOTE.PRICE' | translate }}</p>
              <p class="mb-1">• {{ 'PRODUCT_IMPORT.NOTE.UNIQUE' | translate }}</p>
              <p class="mb-1">• {{ 'PRODUCT_IMPORT.NOTE.BARCODE' | translate }}</p>
              <p class="mb-1">• {{ 'PRODUCT_IMPORT.NOTE.QUANTITY' | translate }}</p>
              <p>• {{ 'PRODUCT_IMPORT.NOTE.ALL_OR_NOTHING' | translate }}</p>
            </div>

            @if (errorMsg()) {
              <div
                class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
              >
                {{ errorMsg() }}
              </div>
            }

            <!--
              Do'kon tanlagich faqat tanlov BO'LGANDA ko'rsatiladi. Do'kon sahifasidan
              ochilganda u allaqachon ma'lum — bir elementli ro'yxatni tanlatish ortiqcha
              qadam va xato manbai bo'lardi.
            -->
            @if (!lockedStoreId()) {
              <div>
                <label
                  for=""
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {{ 'PRODUCT_IMPORT.STORE' | translate }} <span class="text-red-500">*</span>
                </label>
                <select
                  [ngModel]="selectedStoreId()"
                  (ngModelChange)="selectedStoreId.set($event)"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                  [class.border-red-400]="selectedStoreId() === null"
                  [class.border-gray-300]="selectedStoreId() !== null"
                  [class.dark:border-gray-700]="selectedStoreId() !== null"
                >
                  <option [ngValue]="null" disabled>
                    — {{ 'PRODUCT_IMPORT.STORE_PLACEHOLDER' | translate }} —
                  </option>
                  @for (s of stores(); track s.id) {
                    <option [ngValue]="s.id">{{ s.name }}</option>
                  }
                </select>
                @if (selectedStoreId() === null) {
                  <p class="mt-1 text-xs text-red-500">
                    {{ 'PRODUCT_IMPORT.STORE_REQUIRED' | translate }}
                  </p>
                }
              </div>
            }

            <!--
              Valyuta BUTUN FAYL uchun bitta tanlanadi. Faqat bir nechta valyuta
              yoqilgan bo'lsa ko'rinadi: yagona valyutali biznesda bu ikki tanlagich
              hech narsani hal qilmaydigan ortiqcha qadam bo'lardi.
            -->
            @if (currencyOptions().length > 1) {
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label
                    for="imp-sell-cur"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {{ 'PRODUCT_IMPORT.SELL_CURRENCY' | translate }}
                  </label>
                  <select
                    id="imp-sell-cur"
                    [ngModel]="sellCurrency()"
                    (ngModelChange)="sellCurrency.set($event)"
                    class="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    @for (c of currencyOptions(); track c.value) {
                      <option [ngValue]="c.value">{{ c.label }}</option>
                    }
                  </select>
                </div>
                <div>
                  <label
                    for="imp-buy-cur"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {{ 'PRODUCT_IMPORT.PURCHASE_CURRENCY' | translate }}
                  </label>
                  <select
                    id="imp-buy-cur"
                    [ngModel]="purchaseCurrency()"
                    (ngModelChange)="purchaseCurrency.set($event)"
                    class="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    @for (c of currencyOptions(); track c.value) {
                      <option [ngValue]="c.value">{{ c.label }}</option>
                    }
                  </select>
                </div>
              </div>
              <!--
                Valyutalar farq qilsa tannarx kurs bo'yicha o'giriladi va import onida
                MUZLATILADI — keyin kurs o'zgarsa eski mahsulotlar qimirlamaydi.
                Kurs kiritilmagan bo'lsa backend importni butunlay rad etadi.
              -->
              @if (sellCurrency() !== purchaseCurrency()) {
                <p
                  class="-mt-2 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5"
                >
                  <lucide-icon [img]="AlertCircle" class="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  {{ 'PRODUCT_IMPORT.MIXED_CURRENCY_HINT' | translate }}
                </p>
              }
            }

            <div>
              <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {{ 'PRODUCT_IMPORT.FILE' | translate }}
              </label>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                (change)="onFileChange($event)"
                class="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-indigo-900/30 dark:file:text-indigo-300"
              />
              @if (file()) {
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <lucide-icon [img]="FileSpreadsheet" class="w-3.5 h-3.5" />
                  {{ file()?.name }}
                </p>
              }
              @if (previewing()) {
                <p class="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                  {{ 'PRODUCT_IMPORT.READING' | translate }}
                </p>
              }
            </div>

            @if (preview(); as pv) {
              <div class="space-y-3">
                <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    [ngModel]="mapping().hasHeaderRow"
                    (ngModelChange)="setHasHeaderRow($event)"
                    class="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                  />
                  {{ 'PRODUCT_IMPORT.HEADER_ROW' | translate }}
                </label>

                <div
                  class="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700"
                >
                  @for (f of mappingFields; track f.key) {
                    <div class="flex items-center gap-3 px-3 py-2">
                      <span
                        class="flex-1 flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300"
                      >
                        <span
                          class="w-2.5 h-2.5 rounded-full shrink-0 transition-colors"
                          [class.bg-gray-200]="getCol(f.key) === null"
                          [class.dark:bg-gray-600]="getCol(f.key) === null"
                          [style.background-color]="
                            getCol(f.key) !== null ? (fieldDotColors[f.key] ?? '#9ca3af') : null
                          "
                        ></span>
                        {{ f.labelKey | translate }}
                        @if (f.required) {
                          <span class="text-red-500">*</span>
                        }
                      </span>
                      <select
                        [ngModel]="getCol(f.key)"
                        (ngModelChange)="setCol(f.key, $event)"
                        class="w-44 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option [ngValue]="null">
                          — {{ 'PRODUCT_IMPORT.UNMAPPED' | translate }} —
                        </option>
                        @for (col of pv.columns; track $index) {
                          <option [ngValue]="$index">{{ col }}</option>
                        }
                      </select>
                    </div>
                  }
                  @if (hasDuplicateColumns()) {
                    <div
                      class="flex items-center gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400 rounded-b-xl"
                    >
                      <lucide-icon [img]="AlertCircle" class="w-3.5 h-3.5 shrink-0" />
                      {{ 'PRODUCT_IMPORT.DUPLICATE_COLUMNS' | translate }}
                    </div>
                  }
                </div>

                @if (pv.sampleRows.length > 0) {
                  <div
                    class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <table class="w-full text-xs">
                      <thead>
                        <tr
                          class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                        >
                          @for (col of pv.columns; track $index) {
                            <th
                              class="px-2 py-1.5 text-left border-r last:border-0 border-gray-100 dark:border-gray-700 min-w-[80px]"
                            >
                              @if (getColumnAssignment($index); as a) {
                                <span
                                  class="inline-flex items-center px-1.5 py-0.5 rounded text-white text-xs font-medium whitespace-nowrap"
                                  [style.background-color]="a.color"
                                  >{{ a.labelKey | translate }}</span
                                >
                              } @else {
                                <span class="text-gray-400 font-normal">
                                  {{ 'PRODUCT_IMPORT.COLUMN_N' | translate: { n: $index + 1 } }}
                                </span>
                              }
                            </th>
                          }
                        </tr>
                      </thead>
                      <tbody>
                        @for (row of pv.sampleRows; track $index) {
                          <tr
                            class="border-b last:border-0 border-gray-100 dark:border-gray-700"
                            [class.bg-gray-50]="$index === 0 && mapping().hasHeaderRow"
                            [class.dark:bg-gray-800]="$index === 0 && mapping().hasHeaderRow"
                            [class.font-medium]="$index === 0 && mapping().hasHeaderRow"
                          >
                            @for (cell of row; track $index) {
                              <td
                                class="px-2 py-1 whitespace-nowrap text-gray-600 dark:text-gray-300 border-r last:border-0 border-gray-100 dark:border-gray-700"
                                [ngStyle]="getColumnBgStyle($index)"
                              >
                                {{ cell }}
                              </td>
                            }
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                  <p class="text-xs text-gray-400">
                    {{ 'PRODUCT_IMPORT.TOTAL_ROWS' | translate: { n: pv.totalRows } }}
                  </p>
                }
              </div>
            }

            <!--
              Import "hammasi yoki hech narsa": bitta xato bo'lsa ham fayl butunligicha
              qaytariladi. Shuning uchun xato bo'lgan holat MUVAFFAQIYATSIZLIK sifatida
              ko'rsatiladi — "nechta o'tkazib yuborildi" degan yumshoq xabar egasini
              "demak qolgani tushibdi" deb chalg'itardi.
            -->
            @if (result(); as res) {
              <div
                class="p-3 rounded-lg border text-sm"
                [class]="
                  res.errors.length > 0
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                "
              >
                <p class="font-medium">
                  @if (res.errors.length > 0) {
                    {{ 'PRODUCT_IMPORT.REJECTED' | translate: { n: res.errors.length } }}
                  } @else {
                    {{ 'PRODUCT_IMPORT.RESULT' | translate: { created: res.createdCount } }}
                  }
                </p>
                @if (res.errors.length > 0) {
                  <ul class="mt-2 space-y-0.5 max-h-40 overflow-y-auto list-disc list-inside">
                    @for (e of res.errors; track $index) {
                      <li>
                        {{ 'PRODUCT_IMPORT.ROW_N' | translate: { n: e.rowNumber } }}
                        @if (e.name) {
                          <span class="font-medium">{{ e.name }}</span>
                        }
                        — {{ e.message }}
                      </li>
                    }
                  </ul>
                }
              </div>
            }

            <div class="flex gap-3 pt-1">
              <button
                type="button"
                (click)="close()"
                class="flex-1 py-2.5 text-sm border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                {{ 'COMMON.CLOSE' | translate }}
              </button>
              <button
                type="button"
                (click)="submit()"
                [disabled]="!canSubmit()"
                class="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors font-medium"
              >
                <lucide-icon [img]="Upload" class="w-4 h-4" />
                {{
                  importing()
                    ? ('PRODUCT_IMPORT.UPLOADING' | translate)
                    : ('PRODUCT_IMPORT.SUBMIT' | translate)
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class ProductImportDialogComponent {
  private translate = inject(TranslateService);

  visible = model.required<boolean>();
  /** Sarlavha yonidagi kontekst (mijoz nomi yoki do'kon nomi). */
  subtitle = input('');
  stores = input<ImportStoreOption[]>([]);
  /** Berilgan bo'lsa do'kon tanlagich ko'rsatilmaydi va shu do'konga yoziladi. */
  lockedStoreId = input<string | null>(null);

  /**
   * Tanlash mumkin bo'lgan valyutalar. Bo'sh yoki bitta bo'lsa tanlagich umuman
   * ko'rsatilmaydi va backend'ga valyuta yuborilmaydi — u tenant kotirovka
   * valyutasini oladi (eski xulq).
   *
   * <b>Chaqiruvchidan keladi, ichkarida `CurrencyService` dan O'QILMAYDI</b>: admin
   * paneli BOSHQA mijozga import qiladi, admin o'zining valyutalarini ko'rsatsa
   * mijozda yoqilmagan kod tanlanib, import xato bilan tushardi.
   */
  currencyOptions = input<ImportCurrencyOption[]>([]);

  /** Sukut valyuta (tenant kotirovkasi). Bo'sh bo'lsa ro'yxatning birinchisi. */
  defaultCurrency = input<string | null>(null);

  previewFn = input.required<ImportPreviewFn>();
  submitFn = input.required<ImportSubmitFn>();

  imported = output<ProductImportResult>();

  readonly Package = Package;
  readonly X = X;
  readonly Upload = Upload;
  readonly AlertCircle = AlertCircle;
  readonly FileSpreadsheet = FileSpreadsheet;

  file = signal<File | null>(null);
  preview = signal<ExcelPreview | null>(null);
  result = signal<ProductImportResult | null>(null);
  previewing = signal(false);
  importing = signal(false);
  errorMsg = signal('');
  selectedStoreId = signal<string | null>(null);
  mapping = signal<ProductImportMapping>(emptyMapping());

  sellCurrency = signal<string | null>(null);
  purchaseCurrency = signal<string | null>(null);

  readonly mappingFields: {
    key: keyof ProductImportMapping;
    labelKey: string;
    required: boolean;
  }[] = [
    { key: 'nameColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.NAME', required: true },
    { key: 'brandColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.BRAND', required: false },
    {
      key: 'purchasePriceColumn',
      labelKey: 'PRODUCT_IMPORT.FIELDS.PURCHASE_PRICE',
      required: true,
    },
    { key: 'sellPriceColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.SELL_PRICE', required: true },
    { key: 'barcodeColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.BARCODE', required: false },
    { key: 'categoryColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.CATEGORY', required: false },
    { key: 'unitColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.UNIT', required: true },
    { key: 'quantityColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.QUANTITY', required: false },
    { key: 'isWeightColumn', labelKey: 'PRODUCT_IMPORT.FIELDS.IS_WEIGHT', required: false },
  ];

  /**
   * Majburiy ustunlar `mappingFields` dan O'QILADI — ro'yxatga yangi majburiy maydon
   * qo'shilsa, tugmani yoqish sharti o'zi bilan birga yangilanadi. Ilgari bu shart
   * qo'lda sanab yozilgan edi va ikki joy bir-biridan uzoqlashib ketishi mumkin edi.
   */
  private requiredColumnsMapped = computed(() =>
    this.mappingFields.filter((f) => f.required).every((f) => this.mapping()[f.key] !== null),
  );

  readonly fieldDotColors: Partial<Record<keyof ProductImportMapping, string>> = {
    nameColumn: '#3b82f6',
    brandColumn: '#14b8a6',
    purchasePriceColumn: '#f97316',
    sellPriceColumn: '#22c55e',
    barcodeColumn: '#a855f7',
    categoryColumn: '#ec4899',
    unitColumn: '#06b6d4',
    quantityColumn: '#6366f1',
    isWeightColumn: '#eab308',
  };

  constructor() {
    // Oyna har ochilganda toza boshlanadi — oldingi importning fayli, natijasi va
    // moslashtirishi qolib ketsa, kassir bilmasdan eski faylni qayta yuborardi.
    effect(() => {
      if (this.visible()) this.reset();
    });

    // Valyutalar oyna ochilgandan KEYIN kelishi mumkin: admin paneli ularni
    // mijozdan alohida so'rov bilan oladi. `reset()` o'sha payt ro'yxat hali
    // bo'sh bo'lgani uchun tanlovni null qoldirardi va tanlagich bo'sh chiqardi.
    effect(() => {
      const options = this.currencyOptions();
      if (options.length === 0) return;

      const fallback = this.defaultCurrency() ?? options[0].value;
      const isValid = (code: string | null) =>
        code !== null && options.some((o) => o.value === code);

      // Yozishlar `untracked` ichida: aks holda effect o'z o'zgarishidan qayta
      // ishga tushib, cheksiz sikl bo'lardi.
      untracked(() => {
        if (!isValid(this.sellCurrency())) this.sellCurrency.set(fallback);
        if (!isValid(this.purchaseCurrency())) this.purchaseCurrency.set(fallback);
      });
    });
  }

  private reset(): void {
    this.file.set(null);
    this.preview.set(null);
    this.result.set(null);
    this.errorMsg.set('');
    this.previewing.set(false);
    this.importing.set(false);
    this.mapping.set(emptyMapping());
    this.selectedStoreId.set(this.lockedStoreId());

    // Sukut — kotirovka valyutasi. Oldingi importda `$` tanlangani keyingisiga
    // jimgina o'tib ketsa, butun katalog xato valyutada yaratilardi.
    const fallback = this.defaultCurrency() ?? this.currencyOptions()[0]?.value ?? null;
    this.sellCurrency.set(fallback);
    this.purchaseCurrency.set(fallback);
  }

  close(): void {
    this.visible.set(false);
  }

  getCol(key: keyof ProductImportMapping): number | null {
    return this.mapping()[key] as number | null;
  }

  setCol(key: keyof ProductImportMapping, value: number | null): void {
    this.mapping.update((m) => ({ ...m, [key]: value }));
  }

  setHasHeaderRow(value: boolean): void {
    this.mapping.update((m) => ({ ...m, hasHeaderRow: value }));
  }

  private columnAssignments = computed(() => {
    const map = new Map<number, { labelKey: string; color: string }>();
    for (const f of this.mappingFields) {
      const idx = this.mapping()[f.key] as number | null;
      if (idx != null)
        map.set(idx, { labelKey: f.labelKey, color: this.fieldDotColors[f.key] ?? '#9ca3af' });
    }
    return map;
  });

  hasDuplicateColumns = computed(() => {
    const indices = this.mappingFields
      .map((f) => this.mapping()[f.key] as number | null)
      .filter((i): i is number => i != null);
    return new Set(indices).size !== indices.length;
  });

  getColumnAssignment(colIdx: number): { labelKey: string; color: string } | null {
    return this.columnAssignments().get(colIdx) ?? null;
  }

  getColumnBgStyle(colIdx: number): Record<string, string> | null {
    const a = this.columnAssignments().get(colIdx);
    if (!a) return null;
    const h = a.color.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { 'background-color': `rgba(${r},${g},${b},0.12)` };
  }

  canSubmit = computed(
    () =>
      !this.importing() &&
      this.file() != null &&
      this.preview() != null &&
      this.storeId() != null &&
      this.requiredColumnsMapped() &&
      !this.hasDuplicateColumns(),
  );

  private storeId(): string | null {
    return this.lockedStoreId() ?? this.selectedStoreId();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.file.set(file);
    this.preview.set(null);
    this.result.set(null);
    this.mapping.set(emptyMapping());
    this.errorMsg.set('');
    if (!file) return;

    this.previewing.set(true);
    this.previewFn()(file).subscribe({
      next: (res) => {
        this.previewing.set(false);
        if (res.success !== false && res.data) {
          this.preview.set(res.data);
          this.autoDetectMapping(res.data.columns);
        } else {
          this.errorMsg.set(this.messageOf(res));
        }
      },
      error: (err) => {
        this.previewing.set(false);
        this.errorMsg.set(
          err?.error?.message || this.translate.instant('PRODUCT_IMPORT.READ_FAILED'),
        );
      },
    });
  }

  submit(): void {
    const file = this.file();
    const storeId = this.storeId();
    if (!file || !storeId || !this.canSubmit()) return;

    this.importing.set(true);
    this.errorMsg.set('');
    this.result.set(null);

    // Tanlagich ko'rsatilmagan bo'lsa valyuta YUBORILMAYDI (null) — backend tenant
    // kotirovka valyutasini oladi. Bu yerda taxminiy kod yuborilsa, mijozda
    // yoqilmagan valyuta bilan import rad etilishi mumkin edi.
    const useCurrency = this.currencyOptions().length > 1;
    const currencies: ImportCurrencies = {
      sellCurrency: useCurrency ? this.sellCurrency() : null,
      purchaseCurrency: useCurrency ? this.purchaseCurrency() : null,
    };

    this.submitFn()(file, this.mapping(), storeId, currencies).subscribe({
      next: (res) => {
        this.importing.set(false);
        if (res.success !== false && res.data) {
          this.result.set(res.data);
          // Faqat ROSTDAN yaratilganda xabar beriladi: rad etilgan importda hech narsa
          // o'zgarmagan, chaqiruvchi ro'yxatni qayta yuklashi yoki muvaffaqiyat
          // xabarini ko'rsatishi kerak emas.
          if (res.data.createdCount > 0) this.imported.emit(res.data);
        } else {
          this.errorMsg.set(this.messageOf(res));
        }
      },
      error: (err) => {
        this.importing.set(false);
        this.errorMsg.set(err?.error?.message || this.translate.instant('PRODUCT_IMPORT.FAILED'));
      },
    });
  }

  private messageOf(res: { message?: string; errors?: string[] }): string {
    return res.message ?? res.errors?.join(' ') ?? this.translate.instant('PRODUCT_IMPORT.FAILED');
  }

  /**
   * Ustun sarlavhalarini kalit so'zlar bo'yicha taxminiy moslashtirish. Faqat
   * TAKLIF — kassir har maydonni qo'lda o'zgartira oladi.
   */
  private autoDetectMapping(columns: string[]): void {
    const match = (keywords: string[]): number | null => {
      const idx = columns.findIndex((c) => {
        const lc = (c || '').toLowerCase();
        return keywords.some((k) => lc.includes(k));
      });
      return idx >= 0 ? idx : null;
    };

    this.mapping.set({
      ...emptyMapping(),
      nameColumn: match(['nom', 'name', 'tovar', 'mahsulot', 'товар', 'наимен']),
      barcodeColumn: match(['barkod', 'barcode', 'shtrix', 'штрих']),
      purchasePriceColumn: match([
        'kelish',
        'tan narx',
        'kirim',
        'purchase',
        'закуп',
        'приход',
        'себест',
      ]),
      sellPriceColumn: match(['sotish', 'sell', 'narx', 'price', 'продаж', 'цена']),
      categoryColumn: match(['kategor', 'guruh', 'категор', 'тип']),
      unitColumn: match(['birlik', "o'lchov", 'olchov', 'unit', 'единиц', 'ед.изм']),
      quantityColumn: match(['qoldiq', 'miqdor', 'soni', 'qty', 'quantity', 'остат', 'колич']),
      isWeightColumn: match(['tarozi', 'weight', 'вес']),
    });
  }
}

function emptyMapping(): ProductImportMapping {
  return {
    hasHeaderRow: true,
    nameColumn: null,
    brandColumn: null,
    barcodeColumn: null,
    purchasePriceColumn: null,
    sellPriceColumn: null,
    categoryColumn: null,
    unitColumn: null,
    isWeightColumn: null,
    quantityColumn: null,
  };
}
